import type { IMiroJobResult } from "api/miro/miro.type";
import { summarizeCanvasImport, summarizeJobComments, summarizeJobNotes } from "utils/miroCanvas";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The words for a comments-only problem: its own copy per code, a generic one for any other. */
export const commentsWarningKey = (code: string | null | undefined, exists: (key: string) => boolean): string | null => {
  if (!code) return null;
  const key = `miro:comments.errors.${code.replace(/^miro\./, "")}`;

  return exists(key) ? key : "miro:comments.errors.generic";
};

export interface CommentLines {
  /** What went across ("3 comments sent as sticky notes" / "4 comments imported"). */
  done: string | null;
  /** Neutral facts (stickies removed with their deleted threads, notes left out by the cap). */
  notes: string[];
  /** What did not go across, as one soft warning each. */
  warnings: string[];
}

/** The words for a sticky-notes-only problem (REF-7145): its own copy per code, a generic one for any other. */
export const notesWarningKey = (code: string | null | undefined, exists: (key: string) => boolean): string | null => {
  if (!code) return null;
  const key = `miro:notes.errors.${code.replace(/^miro\./, "")}`;

  return exists(key) ? key : "miro:notes.errors.generic";
};

/** The comment lines of a canvas EXPORT result. */
export const exportCommentLines = (t: Translate, exists: (key: string) => boolean, result: IMiroJobResult | null | undefined): CommentLines => {
  const c = summarizeJobComments(result, "export");
  if (!c) return { done: null, notes: [], warnings: [] };

  const notes = c.removed > 0 ? [t("miro:comments.removed", { count: c.removed })] : [];
  const warnings: string[] = [];
  if (c.failed > 0) warnings.push(t("miro:comments.export_failed", { count: c.failed }));
  const why = commentsWarningKey(c.errorCode, exists);
  if (why) warnings.push(t(why));

  return { done: c.done > 0 ? t("miro:comments.exported", { count: c.done }) : null, notes, warnings };
};

/** The comment lines of a canvas IMPORT result. */
export const importCommentLines = (t: Translate, exists: (key: string) => boolean, result: IMiroJobResult | null | undefined): CommentLines => {
  const c = summarizeJobComments(result, "import");
  if (!c) return { done: null, notes: [], warnings: [] };

  const notes = c.skipped > 0 ? [t("miro:comments.skipped", { count: c.skipped })] : [];
  const warnings: string[] = [];
  if (c.failed > 0) warnings.push(t("miro:comments.import_failed", { count: c.failed }));
  const why = commentsWarningKey(c.errorCode, exists);
  if (why) warnings.push(t(why));

  return { done: c.done > 0 || c.duplicate > 0 ? t("miro:comments.imported", { count: c.done, duplicate: c.duplicate }) : null, notes, warnings };
};

/** The sticky-note lines of a canvas EXPORT result (REF-7145: the canvas's notes became Miro stickies). */
export const exportNoteLines = (t: Translate, exists: (key: string) => boolean, result: IMiroJobResult | null | undefined): CommentLines => {
  const n = summarizeJobNotes(result, "export");
  if (!n) return { done: null, notes: [], warnings: [] };

  const warnings: string[] = [];
  if (n.failed > 0) warnings.push(t("miro:notes.export_failed", { count: n.failed }));
  const why = notesWarningKey(n.errorCode, exists);
  if (why) warnings.push(t(why));

  // Said as it happened (a re-export mostly updates or leaves notes as they were): new, updated, unchanged, each when non-zero.
  const c = result?.notes;
  const parts = [
    [c?.created ?? 0, "miro:notes.exported"],
    [c?.updated ?? 0, "miro:notes.export_updated"],
    [c?.unchanged ?? 0, "miro:notes.export_unchanged"],
  ] as const;
  const done = parts
    .filter(([count]) => count > 0)
    .map(([count, key]) => t(key, { count }))
    .join(" ");

  return { done: done || null, notes: [], warnings };
};

/** The sticky-note lines of a canvas IMPORT result (REF-7145: on a realtime canvas each Miro sticky became a note). */
export const importNoteLines = (t: Translate, exists: (key: string) => boolean, result: IMiroJobResult | null | undefined): CommentLines => {
  const n = summarizeJobNotes(result, "import");
  if (!n) return { done: null, notes: [], warnings: [] };

  const notes = n.skipped > 0 ? [t("miro:notes.skipped", { count: n.skipped })] : [];
  const warnings: string[] = [];
  if (n.failed > 0) warnings.push(t("miro:notes.import_failed", { count: n.failed }));
  const why = notesWarningKey(n.errorCode, exists);
  if (why) warnings.push(t(why));

  return { done: n.done > 0 || n.duplicate > 0 ? t("miro:notes.imported", { count: n.done, duplicate: n.duplicate }) : null, notes, warnings };
};

/** Comment lines then note lines, as one set (a job carries one or the other, or both). */
export const joinLines = (...sets: CommentLines[]): CommentLines => ({
  done:
    sets
      .map((l) => l.done)
      .filter(Boolean)
      .join(" ") || null,
  notes: sets.flatMap((l) => l.notes),
  warnings: sets.flatMap((l) => l.warnings),
});

/** Canvas notes a canvas export carried to Miro (created, refreshed or already there) — a notes-only export is a success. */
export const exportedNoteCount = (result: IMiroJobResult | null | undefined): number => summarizeJobNotes(result, "export")?.done ?? 0;

/**
 * A canvas import that produced SOMETHING: images, or (a sticky-only board) comment threads on a classic canvas or notes on
 * a realtime one (REF-7145). Only then does a new canvas exist for the user to open.
 */
export const canvasImportProducedSomething = (result: IMiroJobResult | null | undefined): boolean => {
  if (!result?.canvas) return false;
  const comments = summarizeJobComments(result, "import");
  const notes = summarizeJobNotes(result, "import");

  return summarizeCanvasImport(result).imported > 0 || (comments?.done ?? 0) > 0 || (comments?.duplicate ?? 0) > 0 || (notes?.done ?? 0) > 0 || (notes?.duplicate ?? 0) > 0;
};

/** The comment and note lines of a canvas IMPORT result, as one set. */
export const canvasImportLines = (t: Translate, exists: (key: string) => boolean, result: IMiroJobResult | null | undefined): CommentLines =>
  joinLines(importCommentLines(t, exists, result), importNoteLines(t, exists, result));

/** One sentence-run for a snackbar / tray row: the images line (when there were images, or images that failed), then the comment and note lines. */
export const canvasImportText = (t: Translate, exists: (key: string) => boolean, result: IMiroJobResult | null | undefined): string => {
  const s = summarizeCanvasImport(result);
  const lines = canvasImportLines(t, exists, result);
  const parts = [
    s.imported > 0 || s.failed > 0
      ? t("miro:canvas_import.success", { imported: s.imported, created: s.created, duplicate: s.duplicate, failed: s.failed, skipped: s.skipped, partial: s.partial })
      : null,
    lines.done,
    ...lines.notes,
    ...lines.warnings,
  ];

  return parts.filter(Boolean).join(" ");
};
